import clsx from 'clsx';
import React, { useEffect, VFC } from 'react';
import { Direction, getTrackBackground, Range } from 'react-range';
import { DropdownItemType } from 'types/common';

import styles from './YearsSelector.module.scss';

interface YearsSelectorProps {
  years: DropdownItemType[];
  startYear: number;
  endYear: number;
  onSelect?: (item: DropdownItemType | DropdownItemType[]) => void;
  mobile?: boolean;
}

export const YearsSelector: VFC<YearsSelectorProps> = ({
  years,
  startYear,
  endYear,
  onSelect,
  mobile
}) => {
  const MIN = parseInt(years[years.length - 1].id);
  const MAX = parseInt(years[0].id);
  const rtl = false;

  const [values, setValues] = React.useState([startYear || MIN, endYear || MAX]);

  const handleValuesChange = (values) => {
    setValues(values);

    onSelect &&
      onSelect([
        years.find((year) => year.id === values[0].toString()),
        years.find((year) => year.id === values[1].toString())
      ]);
  };

  // Set values if there's startYear & endYear props
  useEffect(() => {
    setValues([startYear, endYear]);
  }, [startYear, endYear]);

  return (
    <>
      {mobile && (
        <Range
          values={values}
          step={1}
          min={MIN}
          max={MAX}
          rtl={rtl}
          onChange={handleValuesChange}
          renderTrack={({ props, children }) => (
            <div
              className={styles.YearsSelectorMobile__track}
              onMouseDown={props.onMouseDown}
              onTouchStart={props.onTouchStart}
              style={{
                ...props.style
              }}
            >
              <div
                ref={props.ref}
                className={styles.YearsSelectorMobile__trackInner}
                style={{
                  background: getTrackBackground({
                    values,
                    colors: ['#f3f3f3', '#cbcbcb', '#f3f3f3'],
                    min: MIN,
                    max: MAX,
                    rtl
                  })
                }}
              >
                {values[1] !== MAX && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      transform: 'translate(0, -4.5px)',
                      height: '10px',
                      width: '10px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--slider-grey)'
                    }}
                  />
                )}
                {children}
                {values[0] !== MIN && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      transform: 'translate(0, -4.5px)',
                      height: '10px',
                      width: '10px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--slider-grey)'
                    }}
                  />
                )}
              </div>
            </div>
          )}
          renderThumb={({ props, isDragged, index }) => {
            return (
              <div
                {...props}
                style={{
                  ...props.style
                }}
                className={clsx(styles.YearsSelectorMobile__thumb, {
                  [styles['YearsSelectorMobile__thumb--right']]: index === 1,
                  [styles['YearsSelectorMobile__thumb--equalValues']]:
                    index === 1 && values[0] === values[1]
                })}
              >
                <span>{props['aria-valuenow']}</span>
              </div>
            );
          }}
        />
      )}
      {!mobile && (
        <Range
          direction={Direction.Up}
          values={values}
          step={1}
          min={MIN}
          max={MAX}
          rtl={rtl}
          onChange={handleValuesChange}
          renderTrack={({ props, children }) => (
            <div
              className={styles.YearsSelector__track}
              onMouseDown={props.onMouseDown}
              onTouchStart={props.onTouchStart}
              style={{
                ...props.style
              }}
            >
              <div
                ref={props.ref}
                className={styles.YearsSelector__trackInner}
                style={{
                  background: getTrackBackground({
                    values,
                    colors: ['#f3f3f3', '#cbcbcb', '#f3f3f3'],
                    min: MIN,
                    max: MAX,
                    direction: Direction.Up,
                    rtl
                  })
                }}
              >
                {values[1] !== MAX && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      transform: 'translate(-4.5px, -4.5px)',
                      height: '10px',
                      width: '10px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--slider-grey)'
                    }}
                  />
                )}
                {children}
                {values[0] !== MIN && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      transform: 'translate(-4.5px, 4.5px)',
                      height: '10px',
                      width: '10px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--slider-grey)'
                    }}
                  />
                )}
              </div>
            </div>
          )}
          renderThumb={({ props, isDragged, index }) => {
            return (
              <div
                {...props}
                style={{
                  ...props.style
                }}
                className={clsx(styles.YearsSelector__thumb, {
                  [styles['YearsSelector__thumb--bottom']]: index === 0,
                  [styles['YearsSelector__thumb--equalValues']]:
                    index === 0 && values[0] === values[1]
                })}
              >
                <span>{props['aria-valuenow']}</span>
              </div>
            );
          }}
        />
      )}
    </>
  );
};

export default YearsSelector;
